// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useMemo, useState } from 'react';
import { Download, Info, FileSpreadsheet, CalendarDays } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../../../../services/api';
import { getApiBlobErrorMessage } from '../../../../../utils/apiError';
import '../../styles/reports-page.css';

const MAX_FUTURE_DAYS = 30;
const EXCEL_MIME_TYPE = 'application/vnd.ms-excel;charset=utf-8';

const formatDate = (date: Date) => date.toISOString().split('T')[0];

const addDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const useDefaultDates = () => {
  return useMemo(() => {
    const now = new Date();
    const today = formatDate(now);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      defaultStart: formatDate(monthStart),
      defaultEnd: today,
      maxFutureDate: formatDate(addDays(now, MAX_FUTURE_DAYS)),
    };
  }, []);
};

const ReportsPage: React.FC = () => {
  const { defaultStart, defaultEnd, maxFutureDate } = useDefaultDates();
  const [startDate, setStartDate] = useState<string>(defaultStart);
  const [endDate, setEndDate] = useState<string>(defaultEnd);
  const [isDownloading, setIsDownloading] = useState(false);

  const startDateMax = useMemo(() => {
    if (!endDate) return maxFutureDate;
    return new Date(endDate) < new Date(maxFutureDate) ? endDate : maxFutureDate;
  }, [endDate, maxFutureDate]);

  const selectedDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    const s = new Date(startDate);
    const e = new Date(endDate);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || s > e) return 0;
    const diffMs = e.getTime() - s.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
  }, [startDate, endDate]);

  const formatBR = (iso: string) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const clampToFutureLimit = (value: string) => {
    if (!value) return value;
    return new Date(value) > new Date(maxFutureDate) ? maxFutureDate : value;
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (!value) {
      setStartDate(value);
      return;
    }
    setStartDate(new Date(value) > new Date(startDateMax) ? startDateMax : value);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = clampToFutureLimit(event.target.value);
    if (!value) {
      setEndDate(value);
      return;
    }
    setEndDate(value);
    if (startDate && new Date(startDate) > new Date(value)) {
      setStartDate(value);
    }
  };

  const handleDownload = async () => {
    if (!startDate || !endDate) {
      toast.warning('Selecione a data inicial e final para gerar o relatório.');
      return;
    }

    if (startDate > endDate) {
      toast.warning('A data inicial não pode ser maior que a data final.');
      return;
    }

    const futureLimit = new Date(maxFutureDate);
    if (new Date(endDate) > futureLimit || new Date(startDate) > futureLimit) {
      toast.warning(
        `É possível gerar relatórios com até ${MAX_FUTURE_DAYS} dias de antecedência.`
      );
      return;
    }

    setIsDownloading(true);

    try {
      const response = await api.get('/reservo/reservas/relatorio', {
        params: {
          data_inicio: startDate,
          data_fim: endDate,
        },
        responseType: 'blob',
        headers: {
          Accept: 'application/vnd.ms-excel',
        },
      });

      // Backend já envia HTML Excel (.xls); sem reempacotar com `xlsx` no client.
      const reportBlob =
        response.data instanceof Blob
          ? response.data.type
            ? response.data
            : new Blob([response.data], { type: EXCEL_MIME_TYPE })
          : new Blob([response.data], { type: EXCEL_MIME_TYPE });
      const fileName = `reservas_${startDate}_a_${endDate}.xls`;
      const url = window.URL.createObjectURL(reportBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório gerado com sucesso.');
    } catch (error: any) {
      const message = await getApiBlobErrorMessage(error, 'Não foi possível gerar o relatório.');
      toast.error(message);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="reports-page">
      <div className="reports-header">
        <div className="reports-header-text">
          <h1>Relatórios de Reservas</h1>
          <p>
            Exporte um arquivo Excel (.xls) com todos os detalhes das reservas em um intervalo de datas.
          </p>
        </div>
      </div>

      <div className="reports-card">
        <div className="reports-section">
          <div className="reports-section-header">
            <div className="reports-section-title">
              <CalendarDays size={18} />
              <h3>Período do relatório</h3>
            </div>
            {selectedDays > 0 && (
              <span className="reports-range-badge">
                {selectedDays} {selectedDays === 1 ? 'dia' : 'dias'} selecionados
              </span>
            )}
          </div>

          <div className="date-fields">
            <div className="form-group">
              <label htmlFor="start-date">Data inicial</label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                max={startDateMax}
                onChange={handleStartDateChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="end-date">Data final</label>
              <input
                id="end-date"
                type="date"
                value={endDate}
                min={startDate}
                max={maxFutureDate}
                onChange={handleEndDateChange}
              />
            </div>
          </div>
        </div>

        <div className="reports-preview">
          <div className="reports-preview-icon">
            <FileSpreadsheet size={20} />
          </div>
          <div className="reports-preview-body">
            <span className="reports-preview-label">Arquivo a exportar</span>
            <span className="reports-preview-filename">
              reservas_{startDate || '...'}_a_{endDate || '...'}.xls
            </span>
            {startDate && endDate && (
              <span className="reports-preview-range">
                De {formatBR(startDate)} até {formatBR(endDate)}
              </span>
            )}
          </div>
        </div>

        <div className="reports-note">
          <Info size={16} />
          <span>
            O arquivo incluirá todas as reservas confirmadas, canceladas ou
            finalizadas dentro do período selecionado. É possível gerar datas
            futuras de até 30 dias.
          </span>
        </div>

        <div className="reports-actions">
          <button
            type="button"
            className="reports-button"
            onClick={handleDownload}
            disabled={isDownloading}
          >
            <Download size={18} />
            {isDownloading ? 'Gerando…' : 'Gerar Relatório'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
